import React, { useEffect } from "react";
import { IntrospectionFlow } from "../models";
import { useAssist } from "../../../context/AssistContext";

interface IntrospectionWizardProps {
  flow: IntrospectionFlow;
}

export const IntrospectionWizard: React.FC<IntrospectionWizardProps> = ({ flow }) => {
  const {
    state,
    startIntrospection,
    setIntrospectionAnswer,
    goToNextIntrospectionStep,
    goToPreviousIntrospectionStep,
  } = useAssist();

  const active = state.activeIntrospection?.flowId === flow.id ? state.activeIntrospection : undefined;

  useEffect(() => {
    if (!active) startIntrospection(flow);
  }, [active, startIntrospection, flow]);

  if (!active) {
    return (
      <div className="mm-surface p-4">
        <p className="text-sm text-slate-600 dark:text-slate-300">Chargement de l'introspection...</p>
      </div>
    );
  }

  const step = flow.steps[active.currentStepIndex];
  const isLast = active.currentStepIndex === flow.steps.length - 1;
  const isFirst = active.currentStepIndex === 0;

  return (
    <div className="mm-surface p-5 space-y-3">
      <div className="flex items-center justify-between">
        <div>
          <p className="text-[11px] uppercase tracking-wide text-slate-500 dark:text-slate-400">Introspection</p>
          <h3 className="text-lg font-semibold">{flow.title}</h3>
          <p className="text-sm text-slate-600 dark:text-slate-300">{flow.description}</p>
        </div>
        <span className="text-xs text-slate-500 dark:text-slate-400">
          Étape {active.currentStepIndex + 1}/{flow.steps.length}
        </span>
      </div>

      <div className="space-y-2">
        <p className="text-base font-semibold text-slate-800 dark:text-slate-100">{step.title}</p>
        <p className="text-sm text-slate-700 dark:text-slate-200">{step.prompt}</p>
        <input
          type="text"
          value={active.answers[step.id] || ""}
          onChange={(e) => setIntrospectionAnswer(step.id, e.target.value)}
          placeholder={step.placeholder || "Note ta réponse ici..."}
          className="w-full rounded-lg border bg-white dark:bg-slate-900 dark:border-slate-700 text-sm p-3 text-slate-800 dark:text-slate-100"
        />
      </div>

      <div className="flex items-center justify-between">
        <button
          type="button"
          onClick={() => goToPreviousIntrospectionStep(flow)}
          disabled={isFirst}
          className="px-3 py-1.5 rounded-lg border bg-white dark:bg-slate-900 dark:border-slate-700 text-sm disabled:opacity-60"
        >
          Précédent
        </button>
        <button
          type="button"
          onClick={() => goToNextIntrospectionStep(flow)}
          className="px-4 py-1.5 rounded-lg bg-blue-600 text-white text-sm font-semibold hover:bg-blue-700"
        >
          {isLast ? "Terminer" : "Suivant"}
        </button>
      </div>

      {isLast && (
        <div className="rounded-xl border border-slate-200 dark:border-slate-700 p-3 bg-white dark:bg-slate-900 space-y-2">
          <p className="text-sm font-semibold">Bravo pour ces 3 étapes</p>
          <p className="text-sm text-slate-700 dark:text-slate-200">
            Tu peux recommencer ou noter tes réponses ailleurs si tu souhaites les conserver.
          </p>
          <button
            type="button"
            onClick={() => startIntrospection(flow)}
            className="px-3 py-1.5 rounded-lg border bg-white dark:bg-slate-900 dark:border-slate-700 text-sm"
          >
            Recommencer
          </button>
        </div>
      )}
    </div>
  );
};

